import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

interface LlmProviderDropdownProps {
  defaultValue?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const LlmProviderDropdown: React.FC<LlmProviderDropdownProps> = ({
  defaultValue = "openai",
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string>(defaultValue);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled) {
      setSelected(defaultValue);
    }
  }, [defaultValue, disabled]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (value: string) => {
    setSelected(value);
    setOpen(false);
    onChange(value);
  };

  const label = disabled ? t("pages.chat.llmProvider") : t("pages.chat.chooseLlmProvider");

  const displayText = selected === "openai" ? "OpenAI" : "Mistral";

  if (disabled) {
    return (
      <div className="mt-2 flex items-center space-x-2 text-sm text-gray-500">
        <span>{label}:</span>
        <span className="font-semibold text-gray-700">{displayText}</span>
      </div>
    );
  }

  return (
    <div className="mt-2 flex items-center space-x-2 text-sm" ref={dropdownRef}>
      <span>{label}:</span>
      <div className="relative">
        <button onClick={() => setOpen((prev) => !prev)} className="font-semibold pr-6 relative">
          {displayText}
          <span className="absolute right-1 top-1/2 transform -translate-y-1/2">▼</span>
        </button>
        {open && (
          <div className="absolute mt-1 w-24 bg-white border border-gray-300 rounded shadow z-10">
            <div
              className="px-3 py-1 hover:bg-gray-100 cursor-pointer"
              onClick={() => handleSelect("openai")}
            >
              OpenAI
            </div>
            <div
              className="px-3 py-1 hover:bg-gray-100 cursor-pointer"
              onClick={() => handleSelect("mistral")}
            >
              Mistral
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LlmProviderDropdown;
